import { useEffect, useMemo, useState } from 'preact/hooks';
import { useI18n } from '../i18n';
import { RequireAdmin } from '../components/RequireAdmin';
import { useAuth } from '../lib/auth';
import {
  createAdminArticle,
  deleteAdminArticle,
  listAdminArticles,
  listPartnersAdmin,
  updateAdminArticle,
  uploadFile,
  type Article,
  type ArticleDelivery,
  type ArticleKind,
  type Partner,
} from '../lib/admin';

const KINDS: ArticleKind[] = ['drink', 'experience', 'ticket', 'gadget', 'other'];
// All four delivery modes, same set + order the partner self-service page
// (PartnerArticles) offers. Labels mirror the partner i18n wording.
const DELIVERIES: ArticleDelivery[] = ['cantonal', 'national_pickup', 'national', 'digital'];
const DELIVERY_LABELS: Record<ArticleDelivery, string> = {
  cantonal: 'Cantonal — pickup at venue',
  national_pickup: 'National — pickup at any of your locations',
  national: 'National — partner ships',
  digital: 'Digital — sent by email',
};

// What the article modal is doing: composing a brand-new article, editing an
// existing one, or duplicating one (a new article seeded from a source).
type ModalTarget =
  | { mode: 'new' }
  | { mode: 'edit'; source: Article }
  | { mode: 'duplicate'; source: Article };

export function AdminArticles() {
  const { localePath } = useI18n();
  const { accessToken } = useAuth();
  const [articles, setArticles] = useState<Article[] | null>(null);
  const [partners, setPartners] = useState<Partner[]>([]);
  const [partnerFilter, setPartnerFilter] = useState<'' | 'beercup' | number>('');
  const [kindFilter, setKindFilter] = useState<ArticleKind | ''>('');
  const [activeOnly, setActiveOnly] = useState(true);
  const [editing, setEditing] = useState<ModalTarget | null>(null);
  const [err, setErr] = useState<string | null>(null);

  async function load() {
    if (!accessToken) return;
    setErr(null);
    try {
      const list = await listAdminArticles(accessToken, {
        partnerId: partnerFilter === '' ? undefined : partnerFilter,
        kind: kindFilter || undefined,
        activeOnly,
      });
      setArticles(list);
    } catch (e: any) {
      setErr(`${e?.message || e}`);
    }
  }

  useEffect(() => {
    if (!accessToken) return;
    listPartnersAdmin(accessToken).then(setPartners).catch(() => {});
  }, [accessToken]);

  useEffect(() => { load(); }, [accessToken, partnerFilter, kindFilter, activeOnly]);

  return (
    <RequireAdmin>
      <div class="admin-page">
        <div class="admin-shell">
          <header class="admin-header">
            <a class="admin-back" href={localePath('/admin')}>← Admin</a>
            <h1>Articles</h1>
            <div style={{ marginLeft: 'auto' }}>
              <button class="admin-primary" onClick={() => setEditing({ mode: 'new' })}>+ New article</button>
            </div>
          </header>

          <p class="admin-lede">
            Catalog of prize items — drinks, experiences, tickets, gadgets. Each
            article belongs to a partner (or to BeerCup itself). Assignment to
            leaderboard positions happens on the <a class="admin-link" href={localePath('/admin/prizes')}>Prizes</a> page.
          </p>

          <div class="admin-filter-row">
            <select
              class="admin-filter-select"
              value={partnerFilter === '' ? '' : String(partnerFilter)}
              onChange={(e) => {
                const v = (e.target as HTMLSelectElement).value;
                if (v === '') setPartnerFilter('');
                else if (v === 'beercup') setPartnerFilter('beercup');
                else setPartnerFilter(Number(v));
              }}
            >
              <option value="">All partners</option>
              <option value="beercup">BeerCup itself</option>
              {partners.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
            <select
              class="admin-filter-select"
              value={kindFilter}
              onChange={(e) => setKindFilter((e.target as HTMLSelectElement).value as ArticleKind | '')}
            >
              <option value="">All kinds</option>
              {KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
            </select>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'rgba(245,237,224,0.7)', fontSize: 13 }}>
              <input
                type="checkbox"
                checked={activeOnly}
                onChange={(e) => setActiveOnly((e.target as HTMLInputElement).checked)}
              />
              <span>Active only</span>
            </label>
          </div>

          {err && <p class="admin-error">{err}</p>}
          {!articles && !err && <p class="admin-lede">Loading…</p>}

          {articles && articles.length === 0 && (
            <p class="admin-lede">No articles match these filters.</p>
          )}

          {articles && articles.length > 0 && (
            <table class="admin-table">
              <thead>
                <tr>
                  <th />
                  <th>Title</th>
                  <th>Kind</th>
                  <th>Partner</th>
                  <th>Value (CHF)</th>
                  <th>Stock</th>
                  <th>Active</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {articles.map((a) => (
                  <tr key={a.id} style={{ opacity: a.active ? 1 : 0.5 }}>
                    <td>
                      {a.imagePath ? (
                        <img
                          src={a.imagePath}
                          alt=""
                          style={{
                            width: 32, height: 32, objectFit: 'cover',
                            background: '#0e0c0a',
                            border: '1px solid rgba(245,237,224,0.1)',
                            borderRadius: 6,
                          }}
                        />
                      ) : (
                        <span style={{ opacity: 0.3 }}>—</span>
                      )}
                    </td>
                    <td>
                      <div>{a.title}</div>
                      {a.description && (
                        <div class="admin-mono" style={{ fontSize: 10, color: 'rgba(245,237,224,0.5)', maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {a.description}
                        </div>
                      )}
                    </td>
                    <td>{a.kind}</td>
                    <td>{a.partnerName ?? <span style={{ opacity: 0.6 }}>BeerCup</span>}</td>
                    <td class="admin-mono">{a.valueChf != null ? a.valueChf.toFixed(2) : '—'}</td>
                    <td class="admin-mono">{a.stock != null ? a.stock : '—'}</td>
                    <td>{a.active ? 'yes' : 'no'}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                        <button class="admin-link" onClick={() => setEditing({ mode: 'edit', source: a })}>Edit</button>
                        <button class="admin-link" onClick={() => setEditing({ mode: 'duplicate', source: a })}>Duplicate</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {editing && accessToken && (
            <ArticleModal
              mode={editing.mode}
              source={editing.mode === 'new' ? null : editing.source}
              accessToken={accessToken}
              partners={partners}
              onClose={() => setEditing(null)}
              onSaved={async () => {
                setEditing(null);
                await load();
              }}
            />
          )}
        </div>
      </div>
    </RequireAdmin>
  );
}

function ArticleModal({
  mode,
  source,
  accessToken,
  partners,
  onClose,
  onSaved,
}: {
  mode: 'new' | 'edit' | 'duplicate';
  source: Article | null;
  accessToken: string;
  partners: Partner[];
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}) {
  // Edit persists in place; new and duplicate both POST a fresh article — the
  // only difference is that duplicate pre-fills every field from `source`.
  const isEdit = mode === 'edit';
  const isNew = !isEdit;
  // partnerId is fixed for an existing article (the backend doesn't move
  // articles between partners). For new/duplicate, admin picks (seeded from
  // the source when duplicating).
  const [partnerId, setPartnerId] = useState<number | 'beercup'>(
    source ? (source.partnerId ?? 'beercup') : 'beercup',
  );
  const [kind, setKind] = useState<ArticleKind>(source?.kind ?? 'other');
  const [title, setTitle] = useState(source?.title ?? '');
  const [description, setDescription] = useState(source?.description ?? '');
  const [imagePath, setImagePath] = useState(source?.imagePath ?? '');
  const [valueChf, setValueChf] = useState(source?.valueChf?.toString() ?? '');
  const [stock, setStock] = useState(source?.stock?.toString() ?? '');
  const [delivery, setDelivery] = useState<ArticleDelivery>(source?.delivery ?? 'cantonal');
  const [active, setActive] = useState(source?.active ?? true);
  const [err, setErr] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const partnerName = useMemo(() => {
    if (source?.partnerId == null) return source ? 'BeerCup' : '';
    const p = partners.find((x) => x.id === source.partnerId);
    return p?.name ?? `#${source.partnerId}`;
  }, [partners, source]);

  async function onImageFile(e: Event) {
    const input = e.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    setErr(null);
    setUploading(true);
    try {
      const res = await uploadFile(accessToken, file, 'articles');
      setImagePath(res.url);
    } catch (e: any) {
      setErr(e?.message || 'Upload failed');
    } finally {
      setUploading(false);
      input.value = '';
    }
  }

  async function onDelete() {
    if (!isEdit || !source) return;
    if (!confirm(`Soft-delete "${source.title}"? Marks inactive — re-enable via Edit.`)) return;
    setSaving(true);
    try {
      await deleteAdminArticle(accessToken, source.id);
      await onSaved();
    } catch (e: any) {
      setErr(e?.message || 'Delete failed');
    } finally {
      setSaving(false);
    }
  }

  async function submit(e: Event) {
    e.preventDefault();
    setErr(null);
    setSaving(true);
    try {
      if (!title.trim()) throw new Error('Title is required');
      let valueChfNum: number | null = null;
      if (valueChf.trim() !== '') {
        valueChfNum = Number(valueChf);
        if (isNaN(valueChfNum) || valueChfNum < 0) throw new Error('Value (CHF) must be ≥ 0');
      }
      let stockNum: number | null = null;
      if (stock.trim() !== '') {
        stockNum = parseInt(stock, 10);
        if (isNaN(stockNum) || stockNum < 0) throw new Error('Stock must be a non-negative integer');
      }
      const payload = {
        kind,
        title: title.trim(),
        description: description.trim() || undefined,
        imagePath: imagePath.trim() || undefined,
        valueChf: valueChfNum,
        stock: stockNum,
        delivery,
        active,
      };
      if (isEdit) {
        await updateAdminArticle(accessToken, source!.id, payload);
      } else {
        await createAdminArticle(accessToken, {
          ...payload,
          partnerId: partnerId === 'beercup' ? 0 : partnerId,
        });
      }
      await onSaved();
    } catch (e: any) {
      setErr(e?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div class="admin-overlay" onClick={onClose}>
      <form class="admin-modal" style={{ width: 'min(620px, 100%)' }} onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <h2>
          {mode === 'new'
            ? 'New article'
            : mode === 'duplicate'
              ? `Duplicate · ${source!.title}`
              : `Edit · ${source!.title}`}
        </h2>

        {mode === 'duplicate' && (
          <p class="admin-lede" style={{ marginTop: 0 }}>
            Creating a new article from this one. Adjust the stock (or anything
            else) and hit Create — the original is untouched.
          </p>
        )}

        {!isNew && (
          <dl class="admin-meta">
            <div>
              <dt>Partner</dt>
              <dd>{partnerName}</dd>
            </div>
          </dl>
        )}

        {isNew && (
          <label class="admin-field">
            <span>Partner</span>
            <select
              value={partnerId === 'beercup' ? 'beercup' : String(partnerId)}
              onChange={(e) => {
                const v = (e.target as HTMLSelectElement).value;
                setPartnerId(v === 'beercup' ? 'beercup' : Number(v));
              }}
            >
              <option value="beercup">BeerCup itself</option>
              {partners.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </label>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <label class="admin-field">
            <span>Title *</span>
            <input value={title} onInput={(e) => setTitle((e.target as HTMLInputElement).value)} required />
          </label>
          <label class="admin-field">
            <span>Kind</span>
            <select value={kind} onChange={(e) => setKind((e.target as HTMLSelectElement).value as ArticleKind)}>
              {KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
            </select>
          </label>
        </div>

        <label class="admin-field">
          <span>Description</span>
          <textarea rows={3} value={description} onInput={(e) => setDescription((e.target as HTMLTextAreaElement).value)} />
        </label>

        <label class="admin-field">
          <span>Delivery</span>
          <select value={delivery} onChange={(e) => setDelivery((e.target as HTMLSelectElement).value as ArticleDelivery)}>
            {DELIVERIES.map((d) => <option key={d} value={d}>{DELIVERY_LABELS[d]}</option>)}
          </select>
        </label>

        <div class="admin-field">
          <span style={{ textTransform: 'uppercase', fontSize: 10, letterSpacing: '0.06em', color: 'rgba(245,237,224,0.6)', fontFamily: 'IBM Plex Mono, Menlo, monospace' }}>
            Image
          </span>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            {imagePath && (
              <img
                src={imagePath}
                alt=""
                style={{
                  width: 64, height: 64, objectFit: 'cover',
                  background: '#0e0c0a', border: '1px solid rgba(245,237,224,0.1)',
                  borderRadius: 8,
                }}
              />
            )}
            <div style={{ flex: 1, minWidth: 0 }}>
              <input
                type="file"
                accept="image/png,image/jpeg,image/webp,image/svg+xml"
                disabled={uploading}
                onChange={onImageFile}
                style={{ display: 'block', marginBottom: 6, color: 'rgba(245,237,224,0.8)' }}
              />
              <input
                value={imagePath}
                onInput={(e) => setImagePath((e.target as HTMLInputElement).value)}
                placeholder="https://storage.googleapis.com/beercup-uploads/articles/…"
                style={{ width: '100%', fontSize: 11, fontFamily: 'IBM Plex Mono, Menlo, monospace' }}
              />
              {uploading && <p style={{ fontSize: 12, color: 'rgba(245,237,224,0.6)', margin: '4px 0 0' }}>Uploading…</p>}
            </div>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
          <label class="admin-field">
            <span>Value (CHF)</span>
            <input inputMode="decimal" value={valueChf} onInput={(e) => setValueChf((e.target as HTMLInputElement).value)} placeholder="50.00" />
          </label>
          <label class="admin-field">
            <span>Stock</span>
            <input type="number" min={0} value={stock} onInput={(e) => setStock((e.target as HTMLInputElement).value)} placeholder="leave empty = unlimited" />
          </label>
          <label class="admin-field" style={{ flexDirection: 'row', alignItems: 'center', gap: 8, paddingTop: 24 }}>
            <input type="checkbox" checked={active} onChange={(e) => setActive((e.target as HTMLInputElement).checked)} />
            <span style={{ textTransform: 'none', letterSpacing: 0, color: '#f5ede0', fontFamily: 'inherit' }}>Active</span>
          </label>
        </div>

        {err && <p class="admin-error">{err}</p>}

        <div class="admin-modal-actions">
          {!isNew && (
            <button type="button" class="admin-link" onClick={onDelete} style={{ marginRight: 'auto', color: '#ff8b6b' }}>
              Soft delete
            </button>
          )}
          <button type="button" class="admin-link" onClick={onClose}>Cancel</button>
          <button type="submit" class="admin-primary" disabled={saving}>
            {saving ? 'Saving…' : mode === 'edit' ? 'Save' : mode === 'duplicate' ? 'Create copy' : 'Create'}
          </button>
        </div>
      </form>
    </div>
  );
}
